/**
 * Format Utility Functions
 * 
 * This file provides utility functions for formatting and converting distance values.
 */
import { round, toNumber, floor } from 'lodash';

/**
 * Convert a distance value to meters
 * 
 * @param value - The value to convert
 * @param isMeter - Whether the value is already in meters
 * @returns The value in meters, rounded to 4 decimal places
 */
export const toMeter = (value: number, isMeter = false): number => 
  round((isMeter ? value : (value || 0) * 0.3048 || 0), 4);

/**
 * Convert a distance value to feet
 * 
 * @param value - The value to convert
 * @param isMeter - Whether the value is in meters
 * @returns The value in feet, rounded to 2 decimal places
 */
export const toFeet = (value: number, isMeter = false): number => 
  isMeter ? round((value || 0) / 0.3048, 2) || 0 : value;

/**
 * Format a distance value as a string with appropriate units
 * 
 * @param value - The distance value to format
 * @param isMeter - Whether to format in meters (true) or feet/inches (false)
 * @returns Formatted distance string with units
 */
export const distanceToString = (value: number, isMeter = true): string => {
  if (isMeter) {
    const rounded = round(toNumber(value), 1);
    return `${rounded ? rounded : '0'} m`;
  } else {
    value = round(toNumber(value) / 0.3048, 2);
    let feet = floor(value);
    let inches = Math.round(12 * (value - feet)); // Round to whole inches only
    feet += inches === 12 ? 1 : 0;
    inches = inches === 12 ? 0 : inches;
    return `${value ? (feet ? feet + "'" : "") + ((feet && inches) ? " " : "") + (inches ? inches + '"' : "") : "0'"}`;
  }
};

/**
 * Parse a distance string to a number
 * 
 * @param value - The distance string to parse
 * @param isMeter - Whether the string represents meters (true) or feet/inches (false)
 * @returns The parsed distance as a number
 */
export const distanceToNumber = (value: string, isMeter = true): number => {
  if (!value) return 0;
  
  if (isMeter) {
    // Remove any unit suffix (m, meters) and parse
    const cleanValue = value.replace(/\s*(m|meters?)?\s*$/i, '').trim();
    return parseFloat(cleanValue) || 0;
  } else {
    // Handle feet and inches
    const hasInch = value.indexOf('"') > -1;
    const hasFeet = value.indexOf("'") > -1;
    
    // Handle decimal feet (e.g., "9.4")
    if (!hasFeet && !hasInch && /^[\d.]+$/.test(value.trim())) {
      return parseFloat(value) || 0;
    }
    
    const splits = value
      .split(/['" -]/)
      .filter(Boolean)
      .map(x => toNumber(x.trim().match(/^(([0-9]+)?(\.)?([0-9]+)?)$/)?.[0] ?? 0));
    
    return splits.length === 1 
      ? splits[0] / (hasInch ? 12 : 1) 
      : splits[0] + splits[1] / 12;
  }
};

/**
 * Round a distance value to 4 decimal places
 * 
 * @param value - The distance value to round
 * @returns The rounded distance value
 */
export const parseDistance = (value: number): number => round(value, 4);
